import * as Collapsible from '@radix-ui/react-collapsible'
import * as Switch from '@radix-ui/react-switch'
import { useState } from 'react'

import { NumberInput } from '@/components/number-input/numberInput'
import SvgIcon from '@/components/svg-icon/svgIcon'
import { Typography } from '@/components/typography/typography'
import { fontStyles } from '@/components/typography/typographyUtils'
import { cn } from '@/libs/tw'
import { CartService } from '@/stores/cart-store/slices/types'
import { formatCurrency } from '@/utils/formatCurrency'
import { translate } from '@/utils/translator'

import { Service as TService } from '../../types'

type RateServicesProps = {
	services: TService[]
	selectedServices: { id: string; quantity: number }[]
	onSelect: (id: string, quantity?: number) => void
}

export const Services = ({ services, selectedServices, onSelect }: RateServicesProps) => {
	const [open, setOpen] = useState(true)

	return (
		<Collapsible.Root open={open} onOpenChange={setOpen} className={cn('-ml-48 md:m-0')}>
			<div className={cn('flex justify-between pb-12')}>
				<Typography style="p-bold" element="p">
					{translate('room-selection-additions')}
				</Typography>
				<Collapsible.Trigger asChild className={cn('group')}>
					<button className={cn('flex gap-12')}>
						<Typography element="span" style="p">
							{translate('room-selection-details')}
						</Typography>
						<SvgIcon
							collectionName="ibe3"
							iconName="chevron"
							className="h-24 w-24 transition-transform duration-300 ease-[cubic-bezier(0.87,_0,_0.13,_1)] group-data-[state=closed]:rotate-180"
						/>
					</button>
				</Collapsible.Trigger>
			</div>
			<Collapsible.Content className="overflow-hidden data-[state=closed]:animate-slideUpC data-[state=open]:animate-slideDownC">
				<div className={cn('flex flex-col gap-32 divide-y-0')}>
					{services.map((service) => (
						<Service
							service={service}
							onSelect={onSelect}
							checked={Boolean(
								selectedServices.find((s) => s.id === service.serviceCode)
							)}
							serviceQuantity={
								selectedServices.find((s) => s.id === service.serviceCode)
									?.quantity ?? 0
							}
						/>
					))}
				</div>
			</Collapsible.Content>
		</Collapsible.Root>
	)
}

type ServicesProps = {
	service: TService
	checked: boolean
	onSelect: (id: string, quantity?: number) => void
	serviceQuantity: number
}

const Service = ({ service, checked, onSelect, serviceQuantity }: ServicesProps) => {
	const [open, setOpen] = useState(false)
	const [showMore, setShowMore] = useState(false)

	const getServicePricing = (pricing: CartService['servicePricing']) => {
		if (pricing === 'Per night') {
			return translate('room-selection-service-perNight')
		}
		if (pricing === 'Per person') {
			return translate('room-selection-service-perPerson')
		}
		if (pricing === 'Per person per night') {
			return `${translate('room-selection-service-perPerson')} ${translate('room-selection-service-perNight')}`
		}

		return ''
	}

	return (
		<div key={service.serviceCode} className={cn('flex flex-col gap-10')}>
			<div key={service.serviceCode} className={cn('flex items-center justify-between')}>
				<div className={cn('flex gap-12')}>
					<div className={cn('flex flex-col')}>
						<Typography element="p" style="p-bold">
							{service.title}
						</Typography>
						<Typography element="p" style="p">
							{formatCurrency(service.price?.amountAfterTax ?? 0)}{' '}
							<small>{getServicePricing(service.servicePricing)}</small>
						</Typography>
					</div>
				</div>
				{!service.flexibleQuantity && (
					<Switch.Root
						checked={checked}
						onCheckedChange={() =>
							checked
								? onSelect(service.serviceCode, 0)
								: onSelect(service.serviceCode, service.quantity)
						}
						className={cn(
							'group relative h-40 w-[104px] shrink-0 basis-[104px] rounded-32 border border-switch-naturel bg-switch-naturel transition-colors duration-200 data-[state=checked]:bg-switch-bg data-[state=unchecked]:opacity-50 md:border-switch-border'
						)}
					>
						<SvgIcon
							collectionName="ibe3"
							iconName="check"
							className="absolute top-1/2 h-24 w-24 -translate-y-1/2 translate-x-12  text-switch-naturel group-data-[state=checked]:block"
						/>
						<Switch.Thumb
							className={cn(
								'block h-32 w-32 translate-x-2 rounded-[100%] bg-switch-bg transition-transform duration-200 data-[state=checked]:translate-x-[68px] data-[state=checked]:bg-switch-naturel'
							)}
						/>
						<SvgIcon
							collectionName="ibe3"
							iconName="cross"
							className="absolute top-1/2 h-24 w-24 -translate-y-1/2 translate-x-[calc(92px-100%)] text-switch-bg group-data-[state=checked]:hidden"
						/>
					</Switch.Root>
				)}
				{service.flexibleQuantity && (
					<NumberInput
						min={0}
						max={service.quantity}
						value={serviceQuantity}
						onUpdate={(value) => onSelect(service.serviceCode, value)}
					/>
				)}
			</div>
			<div>
				<div
					ref={(el) => {
						if (el && !open) {
							const hasOverflow = el.scrollHeight > el.clientHeight
							setShowMore(hasOverflow)
						}
					}}
					dangerouslySetInnerHTML={{ __html: service.description }}
					className={cn(fontStyles['p'], !open && 'line-clamp-2')}
				/>
				{showMore && !open && (
					<button onClick={() => setOpen(true)}>
						<Typography element="span" style="p" className="lowercase underline">
							{translate('room-selection-service-more')}
						</Typography>
					</button>
				)}
			</div>
		</div>
	)
}
